import { useEffect } from "react";
import {
  Table,
  Card,
  Statistic,
  Button,
  Tag,
  Typography,
  Spin,
  Alert,
} from "antd";
import {
  ReloadOutlined,
  DollarOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAllApexFeesThunk,
  selectApexFees,
} from "@/store/features/fees/apexFeesSlice";

const { Text } = Typography;

export function TransactionHistoryPage() {
  const feeState = useAppSelector(selectApexFees);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchAllApexFeesThunk());
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      completed: "green",
      pending: "orange",
      failed: "red",
    };
    return colors[state] || "default";
  };

  const feeColumns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => {
        if (amount < 0) {
          return (
            <Text strong style={{ color: "#ff4d4f" }}>
              {formatCurrency(amount)}
            </Text>
          );
        } else {
          return (
            <Text strong style={{ color: "#52c41a" }}>
              +{formatCurrency(amount)}
            </Text>
          );
        }
      },
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (state: string) => (
        <Tag color={getStateColor(state)}>{state.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDate,
    },
  ];

  console.debug("Fee state: ", feeState);
  if (feeState.fetchState === "not-started" || !feeState.feeState) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>Loading transaction history...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          id="button-toolbar"
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <Button
            icon={<PrinterOutlined />}
            onClick={() => printComponentAsPDF()}
          >
            Print
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => dispatch(fetchAllApexFeesThunk())}
            loading={feeState.fetchState === "fetching"}
          >
            Refresh
          </Button>
        </div>
      </div>

      {feeState.fetchState === "error" && (
        <Alert
          message="Error"
          description={feeState.errorMessage}
          type="error"
          showIcon
          style={{ marginBottom: "24px" }}
        />
      )}

      {feeState.feeState && (
        <>
          <Card style={{ marginBottom: "24px" }}>
            <Statistic
              title="Current Balance"
              value={formatCurrency(feeState.feeState.current_balance)}
              prefix={<DollarOutlined />}
            />
          </Card>

          <Card>
            <Table
              columns={feeColumns}
              dataSource={feeState.feeState.entries}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} transactions`,
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
}

function printComponentAsPDF() {
  // Find the element with class="outletContainer"
  const element = document.querySelector(".outletContainer");

  if (!element) {
    alert('No element with class="outletContainer" found on this page.');
    return;
  }

  // Create a new window for printing
  const printWindow = window.open("", "_blank");
  if (printWindow === null) {
    return;
  }

  // Create the HTML content for printing
  const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Print - outletContainer</title>
                    <style>
                        @media print {
                            @page {
                                size: A4;
                                margin: 0.5in;
                            }
                            body {
                                margin: 0;
                                padding: 0;
                                font-family: serif;
                                font-size: 12px;
                                line-height: 1.4;
                                color: #000;
                                background: white;
                            }
                            * {
                                box-sizing: border-box;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            .print-container {
                                width: 100%;
                                max-width: 100%;
                                overflow: hidden;
                                word-wrap: break-word;
                            }
                            img {
                                max-width: 100% !important;
                                height: auto !important;
                            }
                            table {
                                width: 100% !important;
                                table-layout: fixed;
                                border-spacing: 8px;
                            }
                            th {
                              text-align: start;
                            }
                            #button-toolbar, .ant-pagination {
                              display: none !important;
                            }
                        }
                        @media screen {
                            table {
                                border-spacing: 8px;
                            }
                            th {
                              text-align: start;
                            }

                            body {
                                font-family: serif;
                                padding: 20px;
                                max-width: 800px;
                                margin: 0 auto;
                            }
                        }
                        .print-container {
                            width: 100%;
                        }

                        #button-toolbar, .ant-pagination {
                          display: none !important;
                        }
                    </style>
                </head>
                <body>
                    <div class="print-container">
                        ${element.outerHTML}
                    </div>
                </body>
                </html>
            `;

  // Write content to the new window
  printWindow.document.write(printContent);
  printWindow.document.close();

  // Wait for content to load, then print
  printWindow.onload = function () {
    setTimeout(() => {
      printWindow.print();
      // Close the window after printing (optional)
      printWindow.onafterprint = function () {
        printWindow.close();
      };
    }, 500);
  };
}
